import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton, 
  TextField,
  Card,
  CardContent,
  Grid,
  styled
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton } from '../../common/styles';
import { MainContainer } from './styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';
import { competencyTemplatesApi } from '../../../services/api';
import { getStatusColor, getStatusTextColor } from '../../../constants/skillTypes';
import { COLORS } from '../../../constants/radar';

// Define predefined colors for the color picker
const PREDEFINED_COLORS = [
  '#CCCCCC', '#FF6B6B', '#FFA94D', '#FFE066', 
  '#FFF3BF', '#C2E0C6', '#85D18A', '#28A745',
];

// Styled component for the color grid
const ColorGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: '8px',
  marginTop: '8px'
}));

// Styled component for color option
const ColorOption = styled(Box)(({ theme, color, selected }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '4px',
  backgroundColor: color,
  cursor: 'pointer',
  border: selected ? '3px solid #333' : '1px solid rgba(0,0,0,0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  }
}));

const TemplateDetail = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [template, setTemplate] = useState(null);
  const [levels, setLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add',
    level: null,
    formData: { name: '', description: '', level: 0, color: '#FF0000' }
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, level: null });

  const fetchTemplate = async () => {
    try {
      setIsLoading(true);
      const data = await competencyTemplatesApi.getAllTemplates();
      const foundTemplate = data.find(t => t.id === templateId);
      
      if (!foundTemplate) {
        showAlert('Template not found', 'error');
        navigate('/skills-matrix/competency-templates');
        return;
      }
      
      setTemplate(foundTemplate);
      setLevels(foundTemplate.levels || []);
    } catch (err) {
      showAlert('Failed to load template details.', 'error', err);
      console.error('Error fetching template:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);

  const handleModalOpen = (mode, level = null) => {
    setModalState({
      isOpen: true,
      mode,
      level,
      formData: level ? {
        name: level.name,
        description: level.description,
        level: parseInt(level.level, 10),
        color: level.color
      } : { 
        name: '', 
        description: '', 
        level: levels.length > 0 ? Math.max(...levels.map(l => parseInt(l.level, 10))) + 1 : 0, 
        color: PREDEFINED_COLORS[0] 
      }
    });
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'add',
      level: null,
      formData: { name: '', description: '', level: 0, color: PREDEFINED_COLORS[0] }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Convert level to integer if it's the level field
    if (name === 'level') {
      const intValue = parseInt(value, 10);
      setModalState(prev => ({
        ...prev,
        formData: { ...prev.formData, [name]: isNaN(intValue) ? 1 : intValue }
      }));
    } else {
      setModalState(prev => ({
        ...prev,
        formData: { ...prev.formData, [name]: value }
      }));
    }
  };

  // Add validation function to check if required fields are filled
  const isFormValid = () => {
    const { name, level, color } = modalState.formData;
    return (
      name.trim() !== '' && 
      level !== null && 
      level !== undefined && 
      Number.isInteger(level) && 
      color.trim() !== ''
    );
  };

  const handleSubmit = async () => {
    try {
      // Validate form data
      if (!modalState.formData.name.trim()) {
        showAlert('Level name is required', 'error');
        return;
      }

      // Create a copy of the form data with level converted to integer
      const formDataWithIntLevel = {
        ...modalState.formData,
        level: parseInt(modalState.formData.level, 10)
      };

      if (modalState.mode === 'add') {
        const payload = {
          ...formDataWithIntLevel,
          competencyTemplateId: templateId
        };
        const response = await competencyTemplatesApi.createLevel(payload);
        showAlert('Competency level created successfully', 'success');
      } else {
        const response = await competencyTemplatesApi.updateLevel(modalState.level.id, formDataWithIntLevel);
        showAlert('Competency level updated successfully', 'success');
      }
      handleModalClose();
      fetchTemplate();
    } catch (err) {
      console.error('Error in level submission:', err);
      showAlert(`Failed to ${modalState.mode === 'add' ? 'create' : 'update'} competency level. ${err.message || 'Unknown error'}`, 'error', err);
    }
  };

  const handleDeleteLevel = (level) => {
    setDeleteModal({ open: true, level });
  };

  const confirmDelete = async () => {
    try {
      await competencyTemplatesApi.deleteLevel(deleteModal.level.id);
      showAlert('Competency level deleted successfully', 'success');
      setDeleteModal({ open: false, level: null });
      fetchTemplate();
    } catch (err) {
      showAlert(`Failed to delete competency level. ${err.message}`, 'error', err);
    }
  };

  const handleColorSelect = (color) => {
    setModalState(prev => ({
      ...prev,
      formData: { ...prev.formData, color }
    }));
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!template) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/skills-matrix/competency-templates')}>
              <FaArrowLeft /> Back to Competency Templates
            </BackButton>
            <Title>Template Not Found</Title>
          </HeaderLeft>
        </Header>
        <Content>
          <Typography>The requested template could not be found.</Typography>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/skills-matrix/competency-templates')}>
            <FaArrowLeft /> Back to Competency Templates
          </BackButton>
          <Title>
            {template.name}
            <Chip 
              label={template.type} 
              size="small" 
              sx={{ 
                ml: 2,
                backgroundColor: getStatusColor(template.type),
                color: getStatusTextColor(template.type),
                fontWeight: 500,
                border: '1px solid',
                borderColor: `${getStatusTextColor(template.type)}20`
              }} 
            />
          </Title>
        </HeaderLeft>
        <HeaderButton 
          onClick={() => handleModalOpen('add')}
          startIcon={<FaPlus />}
        >
          <FaPlus style={{ marginRight: '8px' }} /> Add Level
        </HeaderButton>
      </Header>

      <Content>
        <MainContainer>

          <Box>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {levels.length}
                </span>
                {levels.length === 1 ? 'level' : 'levels'} defined
              </div>
            {levels.length === 0 ? (
              <EmptyState 
                title="No competency levels defined yet" 
                description="Click the '+ Add Level' button to create your first level."
              />
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 3 }}>
                {[...levels].sort((a, b) => a.level - b.level).map((level) => (
                  <Card key={level.id} sx={{ 
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: `1px solid ${level.color}`,
                    boxShadow: 'none',
                    transition: 'all 0.2s ease',
                    opacity: 0.9,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: `${level.color}20`
                    }
                  }}>
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        mb: 2
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: level.color,
                            border: '1px solid transparent'
                          }} />
                          <Typography variant="h6" sx={{ 
                            color: COLORS.PRIMARY,
                            fontSize: '16px',
                            fontWeight: 600
                          }}>
                            Level {level.level}: {level.name}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleModalOpen('edit', level)}
                            color="primary"
                          >
                            <FaEdit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteLevel(level)}
                            color="error"
                          >
                            <FaTrash />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {level.description || <em>No description provided</em>}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </MainContainer>
      </Content>

      {/* Add/Edit Level Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        title={modalState.mode === 'add' ? 'Add Competency Level' : 'Edit Competency Level'}
        onSubmit={handleSubmit}
        submitText={modalState.mode === 'add' ? 'Create' : 'Update'}
        submitDisabled={!isFormValid()}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            name="name"
            required
            fullWidth
            variant="outlined"
            placeholder="Enter level name (e.g. Beginner, Intermediate)"
            value={modalState.formData.name}
            onChange={handleInputChange}
            autoComplete="off"
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Enter level description"
            value={modalState.formData.description}
            onChange={handleInputChange}
            autoComplete="off"
          />
          <TextField
            label="Level Number"
            name="level"
            type="number"
            required
            fullWidth
            variant="outlined"
            placeholder="Enter level number (e.g. 1, 2, 3)"
            value={modalState.formData.level}
            onChange={handleInputChange}
            autoComplete="off"
            InputProps={{ 
              inputProps: { 
                min: -1,
                step: 1
              } 
            }}
          />
          
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Color</Typography>
            <TextField
              label="Color Code"
              name="color"
              required
              fullWidth
              variant="outlined"
              placeholder="Enter color hex code (e.g. #FF0000)"
              value={modalState.formData.color}
              onChange={handleInputChange}
              autoComplete="off"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Box 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      backgroundColor: modalState.formData.color,
                      borderRadius: '50%',
                      border: '1px solid rgba(0,0,0,0.1)',
                      ml: 1,
                      display: 'inline-block',
                      flexShrink: 0,
                      minWidth: '24px'
                    }} 
                  />
                ),
              }}
            />
            <Typography variant="body2" sx={{ mb: 1 }}>Or select from predefined colors:</Typography>
            <ColorGrid>
              {PREDEFINED_COLORS.map((color) => (
                <ColorOption
                  key={color}
                  color={color}
                  selected={modalState.formData.color === color}
                  onClick={() => handleColorSelect(color)}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </ColorGrid>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, level: null })}
        onConfirm={confirmDelete}
        title="Delete Competency Level"
        message={`Are you sure you want to delete the "${deleteModal.level?.name}" level? This action cannot be undone.`}
      />
    </Container>
  );
};

export default TemplateDetail; 